import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, Sparkles, AlertTriangle, PlayCircle, Loader2 } from "lucide-react";

const PRO = new Set(["pro", "enterprise"]);

const PROP_TYPES_CREXI = [
  "Office", "Retail", "Industrial", "Multifamily", "Land",
  "Hotel", "Hospitality", "Mixed Use", "Special Purpose",
  "Healthcare", "Self Storage",
];

/**
 * "Refresh comps for my markets" — Pro/Enterprise auto-pull settings.
 * Backend: GET/PUT /api/comps/autorefresh + POST /api/comps/autorefresh/run-now.
 * Hidden for non-paying users with an upsell card.
 */
export default function CompAutoRefreshSection() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    sources: ["loopnet", "crexi"],
    cities: [],
    states: [],
    property_types_crexi: [],
    max_per_source_per_run: 25,
    sale_or_lease: "sale",
  });
  const [status, setStatus] = useState({});
  const [cityDraft, setCityDraft] = useState("");
  const cityInputRef = useRef(null);

  const isPro = PRO.has((user?.subscription_plan || "").toLowerCase()) || user?.role === "admin";

  useEffect(() => {
    if (!isPro) {
      setLoading(false);
      return;
    }
    api.get("/comps-autorefresh")
      .then((r) => {
        if (r.data?.config) setConfig({ ...config, ...r.data.config });
        if (r.data?.status) setStatus(r.data.status);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/comps-autorefresh", config);
      toast.success("Auto-refresh settings saved");
      if (r.data?.config) setConfig({ ...config, ...r.data.config });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    if (!config.enabled && !window.confirm("Auto-refresh is currently disabled — run a one-shot pull anyway?")) return;
    setRunning(true);
    try {
      const r = await api.post("/comps-autorefresh/run-now", null, { timeout: 240000 });
      const total = r.data?.total_staged ?? 0;
      toast.success(`Run complete — ${total} new listing${total === 1 ? "" : "s"} staged`);
      setStatus(r.data || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const addCity = () => {
    const v = cityDraft.trim();
    if (!v) return;
    if (config.cities.includes(v)) {
      toast.info("Already in list");
      return;
    }
    setConfig({ ...config, cities: [...config.cities, v] });
    setCityDraft("");
    cityInputRef.current?.focus();
  };

  const removeCity = (c) => setConfig({ ...config, cities: config.cities.filter((x) => x !== c) });

  const toggleState = (s) => {
    const has = config.states.includes(s);
    setConfig({ ...config, states: has ? config.states.filter((x) => x !== s) : [...config.states, s] });
  };
  const togglePropertyType = (p) => {
    const has = (config.property_types_crexi || []).includes(p);
    setConfig({ ...config, property_types_crexi: has ? config.property_types_crexi.filter((x) => x !== p) : [...(config.property_types_crexi || []), p] });
  };
  const toggleSource = (src) => {
    const has = config.sources.includes(src);
    setConfig({ ...config, sources: has ? config.sources.filter((x) => x !== src) : [...config.sources, src] });
  };

  if (!isPro) {
    return (
      <section className="bg-gradient-to-br from-violet-50 via-white to-amber-50 border border-violet-200 rounded-sm p-6 mb-6" data-testid="autorefresh-upsell">
        <div className="flex items-start gap-4">
          <div className="bg-violet-600 text-white p-2.5 rounded-sm">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="overline text-violet-700">Pro & Enterprise</div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Auto-refresh comps for your markets</h2>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Let RealtyVault automatically pull fresh CRE listings from LoopNet & Crexi every Monday for the cities and states matching your portfolio. Skip the manual pulls — your comp library grows itself.
            </p>
            <a href="/dashboard/billing" className="btn-primary-dark inline-flex items-center gap-2 mt-3 text-sm" data-testid="autorefresh-upgrade-btn">
              Upgrade to Pro →
            </a>
          </div>
        </div>
      </section>
    );
  }

  if (loading) return null;

  const isEnterprise = (user?.subscription_plan || "").toLowerCase() === "enterprise";
  const cityCap = isEnterprise ? 20 : 5;
  const stateCap = isEnterprise ? 50 : 10;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="autorefresh-section">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline text-slate-500">Premium</div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Auto-refresh comps for my markets
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Weekly pull from LoopNet + Crexi, deduped against your existing library. Runs every Monday at 06:00 UTC. New listings land in the staged-review panel on the Comps page.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="w-4 h-4 accent-emerald-600"
            data-testid="autorefresh-enabled"
          />
          <span className="text-sm font-semibold text-slate-900">{config.enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      {/* Sources */}
      <div className="mb-4">
        <div className="overline text-slate-600 mb-1.5">Sources</div>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "loopnet", l: "LoopNet (cities)" },
            { v: "crexi", l: "Crexi (states)" },
          ].map((s) => {
            const active = config.sources.includes(s.v);
            return (
              <button
                key={s.v}
                type="button"
                onClick={() => toggleSource(s.v)}
                className={`px-3 py-1.5 rounded-full border text-xs ${active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200"}`}
                data-testid={`autorefresh-src-${s.v}`}
              >
                {s.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* Cities (LoopNet) */}
      {config.sources.includes("loopnet") && (
        <div className="mb-4">
          <div className="overline text-slate-600 mb-1.5">LoopNet cities <span className="text-slate-400 normal-case font-sans tracking-normal">(max {cityCap})</span></div>
          <div className="flex gap-2 mb-2">
            <input
              ref={cityInputRef}
              className="input-clean flex-1"
              placeholder="Charlotte, NC"
              value={cityDraft}
              onChange={(e) => setCityDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCity(); } }}
              data-testid="autorefresh-city-input"
            />
            <button onClick={addCity} type="button" className="btn-outline-dark text-sm" data-testid="autorefresh-add-city">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {config.cities.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs font-mono px-2 py-1 rounded-sm" data-testid={`autorefresh-city-chip-${c}`}>
                {c}
                <button onClick={() => removeCity(c)} type="button" className="text-slate-400 hover:text-white">×</button>
              </span>
            ))}
            {config.cities.length === 0 && <span className="text-xs text-slate-400">No cities yet — add one to start pulling LoopNet weekly</span>}
          </div>
        </div>
      )}

      {/* States (Crexi) */}
      {config.sources.includes("crexi") && (
        <div className="mb-4">
          <div className="overline text-slate-600 mb-1.5">Crexi states <span className="text-slate-400 normal-case font-sans tracking-normal">(max {stateCap})</span></div>
          <div className="flex flex-wrap gap-1">
            {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"].map((s) => {
              const active = config.states.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleState(s)}
                  className={`px-2 py-1 rounded-sm border text-xs font-mono ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`autorefresh-state-${s}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Property types (Crexi only) */}
      {config.sources.includes("crexi") && (
        <div className="mb-4">
          <div className="overline text-slate-600 mb-1.5">Crexi property types <span className="text-slate-400 normal-case font-sans tracking-normal">(empty = all)</span></div>
          <div className="flex flex-wrap gap-1.5">
            {PROP_TYPES_CREXI.map((p) => {
              const active = (config.property_types_crexi || []).includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => togglePropertyType(p)}
                  className={`px-2.5 py-1 rounded-full border text-xs ${active ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cap + sale/lease */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="block">
          <div className="overline text-slate-600 mb-1">Listings per source per run</div>
          <input
            type="number"
            min="1"
            max="100"
            className="input-clean w-full"
            value={config.max_per_source_per_run}
            onChange={(e) => setConfig({ ...config, max_per_source_per_run: Number(e.target.value) })}
            data-testid="autorefresh-cap"
          />
          <div className="text-[10px] text-slate-400 font-mono mt-1">~ ${(config.max_per_source_per_run * 0.01).toFixed(2)} Crexi cost per state run</div>
        </label>
        <label className="block">
          <div className="overline text-slate-600 mb-1">Sale or Lease (LoopNet)</div>
          <select className="input-clean w-full" value={config.sale_or_lease} onChange={(e) => setConfig({ ...config, sale_or_lease: e.target.value })}>
            <option value="sale">For Sale</option>
            <option value="lease">For Lease</option>
          </select>
        </label>
      </div>

      {/* Cost-cap warning */}
      <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-3 py-2 mb-4 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Each weekly run costs roughly ${" "}
          {(
            (config.cities.length * (config.sources.includes("loopnet") ? 0 : 0))
            + (config.sources.includes("crexi") ? config.states.length * config.max_per_source_per_run * 0.01 : 0)
          ).toFixed(2)}{" "}
          on Apify (Crexi) plus your RapidAPI quota for LoopNet. Listings already in your library are skipped automatically.
        </div>
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <button onClick={save} disabled={busy} className="btn-primary-dark inline-flex items-center gap-2 disabled:opacity-60" data-testid="autorefresh-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Save
        </button>
        <button onClick={runNow} disabled={running} className="btn-outline-dark inline-flex items-center gap-2 disabled:opacity-60" data-testid="autorefresh-run-now">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run now
        </button>
        {status?.finished_at && (
          <span className="text-xs text-slate-500 font-mono ml-2" data-testid="autorefresh-last-run">
            Last run: {new Date(status.finished_at).toLocaleString()} · {status.total_staged ?? 0} new staged
          </span>
        )}
      </div>
    </section>
  );
}
